/* Narrow adapter for explicit required-option groups in the existing YMQ cart. */
(() => {
  'use strict';
  if (window.CoppertistRequiredCart?.version === 1) return;
  const state = { installed: false, pending: false, error: '' };
  const wrapped = new WeakSet();
  const own = (item) => item?.properties?._wuParentOnly === '1';
  const groupOf = (item) => item?.properties?._YmqChidrenId || '';
  const classify = (cart, key) => {
    const item = cart?.items?.find((line) => line.key === key);
    if (!item) return null;
    if (own(item) && groupOf(item)) return { kind: 'child', item, group: groupOf(item) };
    const group = item.properties?._YmqParentId;
    return group && cart.items.some((line) => own(line) && groupOf(line) === group)
      ? { kind: 'parent', item, group } : null;
  };
  const prefix = (instance) => instance.class_prefix;
  const rowKey = (instance, row) => row?.getAttribute(prefix(instance) + 'item-key');
  const checkoutSelector = '[name="checkout"],.ymq-cart-app-checkout-button,.ymq-cart-app-dynamic-checkout-buttons';
  function lock() {
    const instance = window.ymqCart;
    if (!instance?.cart || typeof instance.class_prefix !== 'string') return;
    const p = prefix(instance);
    document.querySelectorAll('.' + p + 'item').forEach((row) => {
      if (classify(instance.cart, rowKey(instance, row))?.kind !== 'child') return;
      row.querySelectorAll('.' + p + 'item-quantity,.' + p + 'item-remove').forEach((node) => {
        if (!node.hasAttribute('inert')) node.setAttribute('inert', '');
        if (node.getAttribute('aria-disabled') !== 'true') node.setAttribute('aria-disabled', 'true');
      });
      row.querySelectorAll('.' + p + 'item-quantity-input').forEach((node) => { if (!node.disabled) node.disabled = true; });
    });
    document.querySelectorAll(checkoutSelector).forEach((node) => {
      if (state.pending || state.error) {
        if (!node.hasAttribute('inert')) { node.setAttribute('inert', ''); node.dataset.wuCartInertOwned = '1'; }
      } else if (node.dataset.wuCartInertOwned === '1') {
        node.removeAttribute('inert'); delete node.dataset.wuCartInertOwned;
      }
    });
    if (state.error && !document.querySelector('[data-wu-required-cart-error]')) {
      const note = document.createElement('p'); note.dataset.wuRequiredCartError = ''; note.setAttribute('role', 'alert');
      note.textContent = 'Your selected options could not be confirmed. Refresh and review your cart before checkout.';
      (document.querySelector('.' + p + 'items-info') || document.body).prepend(note);
    }
  }
  async function update(instance, row, quantity, expected) {
    const originalQuantity = expected.item.quantity;
    if (state.pending || state.error) { await instance.onQuantityEnd(row, originalQuantity); return; }
    if (expected.kind === 'child') { await instance.onQuantityEnd(row, originalQuantity); lock(); return; }
    state.pending = true; lock();
    try {
      if (!Number.isSafeInteger(quantity) || quantity < 0) throw new Error('Invalid required-option quantity');
      const root = instance.routes_root || window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart.js?ymq-cart-no-listen=1', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart read failed');
      const cart = await response.json();
      const fresh = classify(cart, expected.item.key);
      const parents = cart.items.filter((item) => item.properties?._YmqParentId === expected.group);
      if (!fresh || fresh.kind !== 'parent' || fresh.group !== expected.group || parents.length !== 1
          || parents[0].key !== expected.item.key || fresh.item.quantity < 1) throw new Error('Required-option group changed');
      const children = cart.items.filter((item) => groupOf(item) === expected.group);
      const updates = { [fresh.item.key]: quantity };
      children.forEach((child) => {
        const ratio = child.quantity / fresh.item.quantity;
        if (!Number.isSafeInteger(ratio) || ratio < 1 || !child.key || child.properties?._YmqParentId) throw new Error('Required-option ratio changed');
        updates[child.key] = ratio * quantity;
      });
      const action = instance.param?.action;
      if (typeof action !== 'string' || !/^[a-zA-Z0-9_-]+$/.test(action)) throw new Error('Cart refresh contract unavailable');
      const result = await fetch(root + 'cart/update.js?' + encodeURIComponent(action) + '=updateCartItem', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }),
      });
      if (!result.ok) throw new Error('Cart update failed');
      const current = await result.json();
      if (current.errors || current.status >= 400 || !Array.isArray(current.items)) throw new Error('Cart update failed');
      for (const [key, target] of Object.entries(updates)) {
        const item = current.items.find((line) => line.key === key);
        if (target === 0 ? !!item : !item || item.quantity !== target) throw new Error('Required-option result not confirmed');
      }
      // YMQ's existing fetch listener refreshes its own DOM from this response.
      await instance.onQuantityEnd(row, quantity);
    } catch (error) {
      state.error = error.message || 'Required-option update failed';
      await instance.onQuantityEnd(row, originalQuantity);
    } finally { state.pending = false; lock(); }
  }
  function install() {
    const instance = window.ymqCart;
    if (!instance || wrapped.has(instance) || typeof instance.getBundleUpdateData !== 'function'
        || typeof instance.onQuantityEnd !== 'function' || instance.class_prefix !== 'ymq-cart-app-') return;
    const original = instance.getBundleUpdateData;
    instance.getBundleUpdateData = function (row, quantity, ...rest) {
      const required = classify(this.cart, rowKey(this, row));
      if (!required) return original.call(this, row, quantity, ...rest);
      return update(this, row, quantity, required);
    };
    wrapped.add(instance); state.installed = true; lock();
  }
  function guard(event) {
    if ((state.pending || state.error) && (event.target.closest?.(checkoutSelector)
        || event.type === 'submit' && event.target.querySelector?.(checkoutSelector))) {
      event.preventDefault(); event.stopImmediatePropagation(); return;
    }
    const instance = window.ymqCart;
    if (!instance?.cart || instance.class_prefix !== 'ymq-cart-app-') return;
    const row = event.target.closest?.('.ymq-cart-app-item');
    const current = classify(instance.cart, rowKey(instance, row));
    const blocked = current?.kind === 'child' || (state.pending || state.error) && !!current;
    if (blocked && event.target.closest?.('.ymq-cart-app-item-quantity,.ymq-cart-app-item-remove')) {
      event.preventDefault(); event.stopImmediatePropagation(); lock();
    }
  }
  ['click', 'change', 'submit'].forEach((type) => document.addEventListener(type, guard, true));
  window.CoppertistRequiredCart = { version: 1, get status() { return { ...state }; } };
  install();
  let scheduled = false;
  new MutationObserver(() => {
    if (scheduled) return; scheduled = true;
    queueMicrotask(() => { scheduled = false; install(); lock(); });
  }).observe(document.documentElement, { childList: true, subtree: true });
  // App embeds may initialize after the theme; the observer also handles later carts.
  let attempts = 0;
  const timer = setInterval(() => { install(); if (state.installed || ++attempts >= 60) clearInterval(timer); }, 500);
})();
